import * as React from "react";
import { AlertCircle, CheckCircle2, Image, LayoutTemplate, Sparkles } from "lucide-react";
import { Button, Dialog } from "@/components/ui";
import { SocoAiLogo } from "@/components/Holo";
import { STYLE_CHIPS, STYLE_GROUPS } from "../styleChips";
import { HowBackgroundsWorkDialog } from "./HowBackgroundsWorkDialog";
import type { HelpDialogProps } from "./PromptStudioHelpDialog";

const PERSONALIZED = [
  "The player's name, title, and headshot",
  "Their NMLS and compliance line",
  "Their branch colors, logo, and disclaimer",
  "For review designs: the review quote, stars, reviewer name, and platform, with the best phrase highlighted",
];

const PERSONALIZED_TABLE = [
  { source: "Player", fields: "Name, title, headshot, NMLS and compliance line" },
  { source: "Theme", fields: "Background and text colors, accent color, branch logo, disclaimer" },
  { source: "Review", fields: "Quote with a highlighted phrase, stars, reviewer name, platform" },
];

const FORMATS = [
  "Single image or carousel of 2 to 5 slides",
  "Square 1:1, Portrait 4:5, Story 9:16",
  "Upload a reference image to match a style",
];

const SectionLabel: React.FC<React.PropsWithChildren> = ({ children }) => (
  <span className="text-lg font-semibold text-slate-500 flex items-center gap-1.5 select-none">{children}</span>
);

export const WhatCanIAskDialog: React.FC<HelpDialogProps> = ({ open, onOpenChange }) => {
  const [backgroundsOpen, setBackgroundsOpen] = React.useState(false);
  return (
    <>
      <Dialog open={open} onOpenChange={next => onOpenChange(next)} size="xl">
        <Dialog.Header>
          <Dialog.Title className="flex items-center gap-2 text-xl font-bold font-sans">
            <SocoAiLogo variant="mono" height={16} />
            <span>What can I ask for?</span>
          </Dialog.Title>
          <Dialog.Description className="text-slate-500 text-sm">
            Prompt Studio understands layout, typography, photos, and everything that gets personalized per player.
          </Dialog.Description>
        </Dialog.Header>

        <Dialog.Content className="max-h-[70vh] overflow-y-auto space-y-6 py-4">
          <div className="bg-slate-50 border border-slate-200/50 p-4 rounded-xl text-sm text-slate-600 space-y-2">
            <div className="flex items-center gap-2 font-semibold text-slate-800 text-base">
              <Sparkles className="size-4 text-primary" aria-hidden="true" />
              <span>Every post is personalized</span>
            </div>
            <ul className="list-disc pl-5 space-y-1">
              {PERSONALIZED.map(line => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>

          <div className="space-y-3">
            <SectionLabel>
              <CheckCircle2 className="size-4 text-emerald-500" aria-hidden="true" />
              What gets personalized
            </SectionLabel>
            <div className="border border-slate-200 rounded-xl overflow-hidden text-sm">
              {PERSONALIZED_TABLE.map(row => (
                <div key={row.source} className="grid grid-cols-[120px_1fr] border-b border-slate-200 last:border-b-0">
                  <div className="bg-slate-50 px-3 py-2 font-semibold text-slate-700">{row.source}</div>
                  <div className="px-3 py-2 text-slate-600">{row.fields}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <SectionLabel>
              <Sparkles className="size-4 text-primary" aria-hidden="true" />
              Things to try
            </SectionLabel>
            <p className="text-sm text-slate-600">
              Styles are toggles. You only see the ones that fit the kind of post and format you picked.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-slate-600">
              {STYLE_GROUPS.map(group => (
                <div key={group.id} className="space-y-1">
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{group.label}</div>
                  <ul className="list-disc pl-5 space-y-0.5">
                    {STYLE_CHIPS.filter(chip => chip.group === group.id).map(chip => (
                      <li key={chip.id}>{chip.label}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <SectionLabel>
              <LayoutTemplate className="size-4 text-indigo-600" aria-hidden="true" />
              Formats
            </SectionLabel>
            <ul className="list-disc pl-5 space-y-1 text-sm text-slate-600">
              {FORMATS.map(line => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>

          <div className="space-y-3">
            <SectionLabel>
              <Image className="size-4 text-indigo-600" aria-hidden="true" />
              Backgrounds
            </SectionLabel>
            <p className="text-sm text-slate-600">
              Each slide keeps a few background options from your library, and players pick the one they like.{" "}
              <Button variant="link" size="inline" onClick={() => setBackgroundsOpen(true)}>
                How backgrounds work
              </Button>
            </p>
          </div>

          <div className="flex gap-3 p-4 bg-blue-50/50 border border-blue-100 rounded-xl text-sm text-blue-800 leading-normal">
            <AlertCircle className="size-5 shrink-0 text-blue-500 mt-0.5" aria-hidden="true" />
            <p>Refinements work the same way: describe the change, and apply it to one slide or all of them.</p>
          </div>
        </Dialog.Content>

        <Dialog.Footer>
          <Button onClick={() => onOpenChange(false)}>Close Help</Button>
        </Dialog.Footer>
      </Dialog>
      <HowBackgroundsWorkDialog open={backgroundsOpen} onOpenChange={setBackgroundsOpen} />
    </>
  );
};
